/**
 * WCC360 v2 — capture the Career DNA™ snapshot at the end of the run.
 *
 * Why this exists
 * ---------------
 * The rest of the shell (YoMA, the Career DNA™ surface, the Explorer cards,
 * the roadmap) reads `useCareerDna`, which prefers the sealed
 * `learner_reports` payload and falls back to the on-device snapshot in
 * `localDna`. That snapshot used to be written only by the result page — so a
 * learner who finished the walk and landed on the dashboard instead (which is
 * where the flow now sends them) had a run in their history and an empty
 * Career DNA™ everywhere else.
 *
 * So the snapshot is captured at completion, in the same breath as sealing the
 * session, independent of which screen the learner sees next. This module
 * computes nothing new: it calls the same client preview functions the result
 * page calls, and the sealed report still overrides it the moment it lands.
 */
import type { Band } from "./band";
import type { AssessmentV2State } from "./types";
import { writeLocalDna } from "./localDna";
import { composeHeadline, composeLearner, topArchetypes } from "./results";
import { emptyVector, type ArchetypeVector } from "./archetypes";
import { cloudStorage } from "@/lib/cloudStore";

/**
 * Compute and store the snapshot for a finished run.
 *
 * Always resolves. A failure here costs a preview, never an answer, so it is
 * reported and swallowed rather than surfaced to the learner mid-celebration.
 */
export async function captureDnaSnapshot(band: Band, state: AssessmentV2State): Promise<void> {
  try {
    const tally = (state.part2.archetype_tally as ArchetypeVector) ?? emptyVector();
    const { primary, secondary } = topArchetypes(tally);

    // Same inputs, same order, same fallbacks as the result page.
    const [{ loadCareersBundle }, { bundleToCareers }, { loadMarketIntelligence }, scoring] =
      await Promise.all([
        import("@/data/careersBundle"),
        import("@/lib/careersToScoring"),
        import("@/lib/marketIntelligence"),
        import("@/lib/scoring"),
      ]);
    const [bundle, market] = await Promise.all([loadCareersBundle(), loadMarketIntelligence()]);
    const careers = bundleToCareers(bundle, (id) => market.get(id));

    let learner = composeLearner(state);

    // Explorers (12–14) are never RANKED against job titles and never shown a
    // score. But the rest of the shell (dashboard doors, cards, report,
    // roadmap) has to reflect the run they just finished, so the snapshot
    // carries the same three worlds and the same four "kinds of work to peek
    // at" the result screen shows — computed by the same functions, so the two
    // can never disagree.
    if (band === "12to14") {
      const { scoreWorlds, pickPeekCareers } = await import("./worlds");
      const worlds = scoreWorlds(learner, careers);
      const top3 = worlds.slice(0, 3);
      writeLocalDna(band, {
        archetypePrimary: primary,
        archetypeSecondary: null,
        headline: null,
        worlds: top3.map((w) => ({ name: w.world, blurb: w.blurb })),
        matches: pickPeekCareers(top3).map((c) => ({
          id: c.ciId,
          name: c.title,
          cluster: c.family,
          tier: null,
          explanation: null,
        })),
      });
      return;
    }

    let out = scoring.matchCareers(learner, careers, 15);

    // Eligibility must never return empty: relax study capacity one tier.
    if (out.results.length < 3 && learner.studyCapacity !== "degree") {
      const nextCap =
        learner.studyCapacity === "none" ? "short"
          : learner.studyCapacity === "short" ? "parttime" : "degree";
      learner = { ...learner, studyCapacity: nextCap };
      out = scoring.matchCareers(learner, careers, 15);
    }

    writeLocalDna(band, {
      archetypePrimary: primary,
      archetypeSecondary: secondary,
      headline: composeHeadline(learner, tally),
      matches: out.results.slice(0, 6).map((r) => ({
        id: r.ciId,
        name: r.title,
        cluster: r.family,
        tier: r.tier ?? null,
        explanation: null,
      })),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[wcc360.v2] Career DNA snapshot capture failed:", e);
  }
}

function tallyTotal(state: AssessmentV2State | null): number {
  const tally = state?.part2?.archetype_tally as ArchetypeVector | undefined;
  return tally ? Object.values(tally).reduce((n, v) => n + (Number(v) || 0), 0) : 0;
}

/**
 * Rebuild a missing snapshot for a run the learner has already finished.
 *
 * Two sources, in order:
 *  1. the answers still in this device's own flow state,
 *  2. the response log for the learner's latest completed run — the only
 *     source when they finished on another device, cleared their browser, or
 *     signed in somewhere new. Without it every surface that reads Career
 *     DNA™ showed an empty state to someone who had answered everything.
 *
 * Returns true when a snapshot was written. Nothing here scores: it replays
 * recorded answers through the same client preview the result page uses.
 */
export async function backfillDnaSnapshot(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const { storageKey } = await import("./band");
  const bands: Band[] = ["18plus", "15to18", "12to14"];

  for (const band of bands) {
    let state: AssessmentV2State | null = null;
    try {
      const raw = cloudStorage.getItem(storageKey(band));
      state = raw ? (JSON.parse(raw) as AssessmentV2State) : null;
    } catch { state = null; }
    if (!state?.part2) continue;

    // Only a run that reached the archetype tally can produce a snapshot;
    // anything less would be a guess, and we never guess a result.
    if (!tallyTotal(state)) continue;

    await captureDnaSnapshot(band, state);
    return true;
  }

  try {
    const { loadLatestCompletedRun } = await import("./persistedSnapshot");
    const run = await loadLatestCompletedRun();
    if (run && tallyTotal(run.state)) {
      await captureDnaSnapshot(run.state.band, run.state);
      return true;
    }
  } catch {
    // A replay that fails costs a preview, never an answer.
  }

  return false;
}
